import React from 'react';
import { getDynatraceLogger } from '@/Services/dynatrace-logger';

type LazyImport<TComponent extends React.ComponentType<any>> = () => Promise<{
  default: TComponent;
}>;

const CHUNK_RELOAD_STORAGE_PREFIX = 'withdrawals-app:chunk-reload';

const retryDelay = (milliseconds: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

export const isDynamicImportError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);

  return [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Loading chunk',
    'ChunkLoadError',
  ].some((pattern) => message.includes(pattern));
};

export const safePreload = async (
  importer: () => Promise<unknown>,
  moduleName: string
): Promise<void> => {
  try {
    await importer();
  } catch (error) {
    const logger = getDynatraceLogger();
    const normalizedError = error instanceof Error ? error : new Error(String(error));

    logger?.logWarning('Lazy preload failed', {
      'module.name': moduleName,
      'exception.type': normalizedError.name,
      'exception.message': normalizedError.message,
      'error.type': isDynamicImportError(error) ? 'dynamic-import' : 'preload',
    });
  }
};

export const lazyWithRetry = <TComponent extends React.ComponentType<any>>(
  importer: LazyImport<TComponent>,
  moduleName: string
) =>
  React.lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (!isDynamicImportError(error)) {
        throw error;
      }

      await retryDelay(750);

      try {
        return await importer();
      } catch (retryError) {
        const reloadKey = `${CHUNK_RELOAD_STORAGE_PREFIX}:${moduleName}:${window.location.pathname}`;
        const hasReloaded = window.sessionStorage.getItem(reloadKey) === 'true';

        if (!hasReloaded) {
          window.sessionStorage.setItem(reloadKey, 'true');
          window.location.reload();

          return new Promise(() => undefined);
        }

        throw retryError;
      }
    }
  });
